import { cn } from '../../lib/cn.js'
import { NOT_RECORDED } from '../../lib/words.js'
import { Text } from '../ui/Typography.js'
import { Skeleton } from '../ui/Skeleton.js'

export interface Fact {
  label: React.ReactNode
  /** `null` and `undefined` both draw "Not recorded", in words. */
  value: React.ReactNode
}

/**
 * The facts on a record panel.
 *
 * **A missing value says "Not recorded" rather than leaving a gap.** Null and zero are
 * different things and a blank is neither — it reads as a screen that failed to
 * load. The product's own rule, applied here so no panel has to remember it.
 */
export function DefinitionList({
  facts,
  columns = 2,
  layout = 'grid',
  loading,
  loadingRows = 4,
  className,
}: {
  /** `undefined` means it has not arrived yet. */
  facts: Fact[] | undefined
  columns?: 1 | 2 | 3
  /**
   * `grid` stacks each label over its value in columns — right where the facts
   * are the page, as on a provider's details.
   *
   * `rows` is the prototype's record pattern: one fact per line inside a
   * hairlined list, the label on the left and the value on the right. It reads
   * as a small table of facts rather than as a paragraph of pairs, and it is
   * what every record panel in the working prototype uses.
   */
  layout?: 'grid' | 'rows'
  loading?: boolean
  loadingRows?: number
  className?: string
}) {
  /**
   * The same skeleton rule as the table and the history it sits beside.
   *
   * A record panel is three components fed by one request. Two of them drew a
   * shape while it loaded and this one drew nothing, so the panel grew by the
   * height of the facts the moment the response arrived and pushed everything
   * below it down the page.
   */
  if (loading || !facts) {
    facts = Array.from({ length: loadingRows }, () => ({
      label: <Skeleton className="h-3 w-20" />,
      value: <Skeleton className="h-4 w-32" />,
    }))
  }
  if (layout === 'rows') {
    return (
      <dl
        className={cn('border-hairline divide-hairline m-0 divide-y rounded-md border', className)}
      >
        {facts.map((f, i) => (
          <div key={i} className="flex items-baseline justify-between gap-5 px-4 py-3">
            <dt className="shrink-0">
              <Text as="span" variant="secondary">
                {f.label}
              </Text>
            </dt>
            {/* `justify-end` reaches inside, because a value can be a
                `WordList` — a flex `<ul>`, which `text-align` does nothing to. */}
            <dd className="m-0 min-w-0 text-right [&_ul]:justify-end">
              <Text as="span" variant="body">
                {f.value === null || f.value === undefined || f.value === '' ? (
                  <span className="text-ink-muted">{NOT_RECORDED}</span>
                ) : (
                  f.value
                )}
              </Text>
            </dd>
          </div>
        ))}
      </dl>
    )
  }
  return (
    <dl
      className={cn(
        'grid gap-5',
        columns === 1 && 'grid-cols-1',
        columns === 2 && 'grid-cols-1 sm:grid-cols-2',
        columns === 3 && 'grid-cols-1 sm:grid-cols-3',
        className,
      )}
    >
      {facts.map((f, i) => (
        <div key={i} className="flex flex-col gap-1">
          <dt>
            <Text as="span" variant="caption">
              {f.label}
            </Text>
          </dt>
          <dd className="m-0">
            <Text as="span" variant="body-strong">
              {f.value === null || f.value === undefined || f.value === '' ? NOT_RECORDED : f.value}
            </Text>
          </dd>
        </div>
      ))}
    </dl>
  )
}
